<template>
  <div class="artist-albums-list mb-2">
    <!-- Панель: фильтры и массовые действия -->
    <div class="d-flex flex-wrap align-items-center mb-1">
      <b-form-select
          v-model="typeFilter"
          :options="typeFilterOptions"
          size="sm"
          class="albums-type-filter mr-1 mb-50"
      />
      <b-form-checkbox v-model="includeDisabled" class="mr-2 mb-50" @change="reload">
        Показывать выключенные
      </b-form-checkbox>

      <div v-if="canManage" class="ml-auto d-flex flex-wrap">
        <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-50 mb-50"
            :disabled="isBusy"
            @click="runBulkAction('bindAlbumCovers', 'Привязать обложки', bindCoversMessage)"
        >
          Привязать обложки
        </b-button>
        <b-button
            variant="outline-secondary"
            size="sm"
            class="mr-50 mb-50"
            :disabled="isBusy"
            @click="runBulkAction('rematchAlbumTracks', 'Пересопоставить треки', rematchMessage)"
        >
          Пересопоставить треки
        </b-button>
        <span v-b-tooltip.hover :title="hasMusicBrainz ? '' : 'У артиста нет привязки к MusicBrainz'" class="mr-50 mb-50">
          <b-button
              variant="outline-secondary"
              size="sm"
              :disabled="isBusy || !hasMusicBrainz"
              @click="runBulkAction('refineAlbumsMusicBrainz', 'Уточнить по MusicBrainz', musicBrainzMessage)"
          >
            Уточнить по MusicBrainz
          </b-button>
        </span>
        <b-button variant="outline-primary" size="sm" class="mb-50" :disabled="isBusy" @click="openSuggestions">
          Подсказки связей
        </b-button>
      </div>
    </div>

    <div v-if="isLoading && !isLoaded" class="text-center py-2">
      <b-spinner small />
    </div>
    <div v-else-if="isLoaded && !rows.length" class="text-muted py-1">
      Альбомов в БД пока нет
    </div>

    <b-overlay v-else-if="isLoaded" :show="isLoading" rounded="sm" opacity="0.5">
      <div class="table-responsive">
        <table class="table table-sm albums-table mb-0">
          <thead>
            <tr>
              <th style="width: 56px" />
              <th>Альбом</th>
              <th style="width: 190px">Тип</th>
              <th style="width: 90px">Год</th>
              <th style="width: 100px">Треки в БД</th>
              <th style="width: 80px">Источники</th>
              <th style="width: 70px">Вкл</th>
              <th v-if="canManage" style="width: 40px" />
            </tr>
          </thead>
          <tbody>
            <template v-for="row in rows">
              <tr :key="row.key" :class="{'album-disabled': !row.album.is_enabled, 'album-nested': row.depth > 0}">
                <td>
                  <span
                      class="album-thumb"
                      :class="{'album-thumb-clickable': row.album.processed_cover || row.album.cover_url}"
                      @click="openCoverPreview(row.album)"
                  >
                    <img
                        v-if="row.album.processed_cover"
                        :src="row.album.processed_cover.url"
                        alt=""
                        loading="lazy"
                    >
                    <img v-else-if="row.album.cover_url" :src="row.album.cover_url" alt="" loading="lazy">
                    <span v-else class="album-thumb-empty" />
                    <span v-if="row.album.processed_cover" class="album-thumb-ok" title="Обработанная обложка в БД">
                      <feather-icon icon="CheckIcon" size="10" />
                    </span>
                  </span>
                </td>
                <td>
                  <div :class="{'pl-2': row.depth > 0}">
                    <a href="#" class="album-title" @click.prevent="toggleTracks(row.album)">
                      <feather-icon :icon="isTracksExpanded(row.album.id) ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="14" />
                      <b>{{ row.album.title }}</b>
                    </a>
                    <div v-if="row.album.artists_label" class="text-muted font-small-2">{{ row.album.artists_label }}</div>
                    <div v-if="row.depth === 0 && (row.album.editions.length || row.album.singles.length)" class="font-small-2">
                      <a
                          v-if="row.album.editions.length"
                          href="#"
                          class="mr-1"
                          @click.prevent="toggleNested(row.album.id, 'editions')"
                      >
                        {{ isNestedExpanded(row.album.id, 'editions') ? '−' : '+' }}{{ row.album.editions.length }} {{ pluralEditions(row.album.editions.length) }}
                      </a>
                      <a
                          v-if="row.album.singles.length"
                          href="#"
                          @click.prevent="toggleNested(row.album.id, 'singles')"
                      >
                        {{ isNestedExpanded(row.album.id, 'singles') ? '−' : '+' }}{{ row.album.singles.length }} {{ pluralSingles(row.album.singles.length) }}
                      </a>
                    </div>
                    <div v-if="row.depth > 0 && row.kindLabel" class="text-muted font-small-2">{{ row.kindLabel }}</div>
                  </div>
                </td>
                <td>
                  <b-form-select
                      :value="row.album.type"
                      :options="typeOptions"
                      size="sm"
                      :disabled="!canManage"
                      @change="value => updateAlbumField(row.album, {type: value})"
                  />
                  <small v-if="row.album.is_type_manual" class="text-muted">вручную</small>
                </td>
                <td>
                  <b-form-input
                      v-if="editingYearId === row.album.id"
                      v-model="yearDraft"
                      type="number"
                      size="sm"
                      min="1900"
                      max="2100"
                      autofocus
                      @keydown.enter.prevent="saveYear(row.album)"
                      @keydown.esc.prevent="editingYearId = null"
                      @blur="saveYear(row.album)"
                  />
                  <span
                      v-else
                      :class="{'cursor-pointer': canManage, 'text-muted': !row.album.is_year_manual}"
                      :title="canManage ? 'Нажмите, чтобы указать год оригинала вручную' : ''"
                      @click="startEditYear(row.album)"
                  >
                    {{ row.album.effective_year || '—' }}
                  </span>
                </td>
                <td>
                  {{ row.album.tracks_in_db }} / {{ trackTotal(row.album) }}
                </td>
                <td>
                  <span v-if="row.album.externals.spotify" class="mr-50" title="Spotify">
                    <source-icon source="spotify" :size="16" />
                  </span>
                  <span v-if="row.album.externals.yandex" class="mr-50" title="Яндекс.Музыка">
                    <source-icon source="yandex" :size="16" />
                  </span>
                  <span v-if="row.album.externals.musicbrainz" class="badge badge-light-secondary" title="MusicBrainz">MB</span>
                </td>
                <td>
                  <b-form-checkbox
                      :checked="row.album.is_enabled"
                      switch
                      :disabled="!canManage"
                      @change="value => updateAlbumField(row.album, {is_enabled: value})"
                  />
                </td>
                <td v-if="canManage">
                  <b-dropdown variant="link" no-caret right toggle-class="p-0" boundary="window">
                    <template #button-content>
                      <feather-icon icon="MoreVerticalIcon" size="16" />
                    </template>
                    <b-dropdown-item v-if="canViewCovers" @click="openCoverModal(row.album)">
                      Выбрать обложку
                    </b-dropdown-item>
                    <b-dropdown-item v-if="row.album.processed_cover" @click="unbindCover(row.album)">
                      Отвязать обложку
                    </b-dropdown-item>
                    <b-dropdown-item @click="openRelationsModal(row.album)">Связи</b-dropdown-item>
                    <b-dropdown-item @click="openMergeModal(row.album)">Объединить с…</b-dropdown-item>
                    <b-dropdown-divider />
                    <b-dropdown-item variant="danger" @click="removeAlbum(row.album)">Удалить</b-dropdown-item>
                  </b-dropdown>
                </td>
              </tr>

              <!-- Трек-лист альбома -->
              <tr v-if="isTracksExpanded(row.album.id)" :key="row.key + '-tracks'">
                <td :colspan="canManage ? 8 : 7" class="p-0 border-top-0">
                  <div class="album-tracklist">
                    <div v-if="tracksLoading[row.album.id]" class="py-50"><b-spinner small /></div>
                    <div v-else-if="!(tracksByAlbum[row.album.id] || []).length" class="text-muted py-50">
                      Трек-лист пуст
                    </div>
                    <template v-else>
                    <div
                        v-for="albumTrack in (tracksByAlbum[row.album.id] || [])"
                        :key="albumTrack.id"
                        class="album-track-row"
                        :class="{'text-muted': !albumTrack.track_id}"
                    >
                      <span class="album-track-number">{{ trackNumber(row.album.id, albumTrack) }}</span>
                      <span class="album-track-title" :class="{'font-italic': !albumTrack.track_id}">
                        {{ albumTrack.title }}
                      </span>
                      <template v-if="albumTrack.track_id">
                        <b-button
                            v-if="albumTrack.track_download_url"
                            variant="flat-secondary"
                            size="sm"
                            class="btn-icon p-25 ml-25"
                            :title="isPlaying(albumTrack) ? 'Остановить' : 'Прослушать'"
                            @click="playAlbumTrack(row.album, albumTrack)"
                        >
                          <feather-icon :icon="isPlaying(albumTrack) ? 'PauseIcon' : 'PlayIcon'" size="14" />
                        </b-button>
                        <router-link
                            v-if="$can('view', 'track-view')"
                            :to="{name: 'track-detail', params: {id: albumTrack.track_id}}"
                            class="ml-50 font-small-2"
                        >
                          в БД
                        </router-link>
                        <span v-else class="ml-50 font-small-2 text-success">в БД</span>
                      </template>
                      <span v-else class="ml-50 font-small-2">нет в БД</span>
                    </div>
                    </template>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </b-overlay>

    <!-- Модалка: выбор обложки -->
    <b-modal
        v-model="coverModal.show"
        title="Выбрать обложку"
        size="lg"
        hide-footer
        lazy
    >
      <div v-if="coverModal.loading" class="text-center py-2"><b-spinner small /></div>
      <div v-else-if="!coverModal.covers.length" class="text-muted">
        У артиста нет сохранённых обложек
      </div>
      <div v-else class="album-cover-grid">
        <a
            v-for="cover in coverModal.covers"
            :key="cover.id"
            href="#"
            class="album-cover-choice"
            :title="cover.name"
            @click.prevent="bindCover(cover)"
        >
          <img :src="cover.url" alt="" loading="lazy">
          <small class="d-block text-truncate">{{ cover.name }}</small>
        </a>
      </div>
    </b-modal>

    <!-- Модалка: связи альбома -->
    <b-modal
        v-model="relationsModal.show"
        :title="relationsModal.album ? 'Связи: ' + relationsModal.album.title : 'Связи'"
        hide-footer
        lazy
    >
      <template v-if="relationsModal.album">
        <div v-if="!currentRelations.length" class="text-muted mb-1">Связей нет</div>
        <div v-for="relation in currentRelations" :key="relation.id" class="d-flex align-items-center mb-50">
          <span>{{ relation.type_label }} → «{{ relation.related_title || ('#' + relation.related_album_id) }}»</span>
          <b-button
              variant="flat-danger"
              size="sm"
              class="btn-icon ml-auto p-25"
              title="Удалить связь"
              @click="removeRelation(relation)"
          >
            <feather-icon icon="TrashIcon" size="14" />
          </b-button>
        </div>

        <hr>
        <div class="font-weight-bold mb-50">Добавить связь</div>
        <b-form-group label="Этот альбом —" label-size="sm">
          <b-form-select v-model="relationsModal.type" :options="relationTypeOptions" size="sm" />
        </b-form-group>
        <b-form-group label="Оригинал / основной альбом" label-size="sm">
          <b-form-select
              v-model="relationsModal.relatedAlbumId"
              :options="otherAlbumOptions(relationsModal.album.id)"
              size="sm"
          />
        </b-form-group>
        <b-button
            variant="primary"
            size="sm"
            :disabled="!relationsModal.relatedAlbumId || isBusy"
            @click="addRelation"
        >
          Добавить
        </b-button>
      </template>
    </b-modal>

    <!-- Модалка: объединение -->
    <b-modal
        v-model="mergeModal.show"
        :title="mergeModal.album ? 'Объединить с… (' + mergeModal.album.title + ')' : 'Объединить'"
        ok-title="Объединить"
        cancel-title="Отмена"
        :ok-disabled="!mergeModal.sourceAlbumId || isBusy"
        lazy
        @ok.prevent="confirmMerge"
    >
      <p class="text-muted font-small-3">
        Выбранный альбом будет влит в текущий и удалён: трек-лист останется текущего,
        привязки и связи переедут.
      </p>
      <b-form-select
          v-if="mergeModal.album"
          v-model="mergeModal.sourceAlbumId"
          :options="otherAlbumOptions(mergeModal.album.id)"
          size="sm"
      />
    </b-modal>

    <!-- Модалка: подсказки связей -->
    <b-modal
        v-model="suggestionsModal.show"
        title="Подсказки связей"
        size="lg"
        hide-footer
        lazy
    >
      <div v-if="suggestionsModal.loading" class="text-center py-2"><b-spinner small /></div>
      <div v-else-if="!suggestionsModal.items.length" class="text-muted">Подсказок нет</div>
      <template v-else>
      <div
          v-for="suggestion in suggestionsModal.items"
          :key="suggestion.album_id + '-' + suggestion.related_album_id + '-' + suggestion.type"
          class="d-flex align-items-center border-bottom py-50"
      >
        <div>
          <div>
            «{{ albumTitle(suggestion.album_id) }}» — {{ relationTypeLabel(suggestion.type) }} «{{ albumTitle(suggestion.related_album_id) }}»
          </div>
          <small class="text-muted">{{ suggestion.reason }}</small>
        </div>
        <div class="ml-auto text-nowrap pl-1">
          <b-button variant="success" size="sm" class="mr-50" :disabled="isBusy" @click="acceptSuggestion(suggestion)">
            Принять
          </b-button>
          <b-button variant="outline-secondary" size="sm" :disabled="isBusy" @click="dismissSuggestion(suggestion)">
            Отклонить
          </b-button>
        </div>
      </div>
      </template>
    </b-modal>

    <!-- Модалка: предпросмотр обложки -->
    <b-modal
        v-model="coverPreview.show"
        :title="coverPreview.album ? coverPreview.album.title : ''"
        size="lg"
        hide-footer
        centered
        lazy
    >
      <div v-if="coverPreview.album" class="d-flex flex-wrap justify-content-center" style="gap: 1rem;">
        <figure v-if="coverPreview.album.processed_cover" class="album-cover-preview">
          <img :src="coverPreview.album.processed_cover.url" alt="">
          <figcaption class="text-muted small">Обработанная обложка в БД</figcaption>
        </figure>
        <figure v-if="coverPreview.album.cover_url" class="album-cover-preview">
          <img :src="coverPreview.album.cover_url" alt="">
          <figcaption class="text-muted small">Обложка с площадки</figcaption>
        </figure>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BDropdown,
  BDropdownDivider,
  BDropdownItem,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BOverlay,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { toggleTrack, isTrackPlaying } from '@/layouts/components/globalPlayer'
import SourceIcon from './SourceIcon.vue'

// Значения и подписи — как AlbumTypeEnum / AlbumRelationTypeEnum на бэкенде
const ALBUM_TYPES = [
  { value: 'studio', text: 'Студийный' },
  { value: 'ep', text: 'EP' },
  { value: 'single', text: 'Сингл' },
  { value: 'live', text: 'Live' },
  { value: 'compilation', text: 'Компиляция' },
  { value: 'soundtrack', text: 'Саундтрек' },
  { value: 'remix', text: 'Ремиксы' },
  { value: 'cover', text: 'Кавер-альбом/трибьют' },
  { value: 'demo', text: 'Демо/бутлег' },
]

const RELATION_TYPES = [
  { value: 'remaster_of', text: 'Ремастер' },
  { value: 'deluxe_of', text: 'Делюкс' },
  { value: 'single_from', text: 'Сингл с альбома' },
]

export default {
  name: 'ArtistAlbumsList',
  components: {
    BButton,
    BDropdown,
    BDropdownDivider,
    BDropdownItem,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BModal,
    BOverlay,
    BSpinner,
    SourceIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    artist: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      albums: [],
      isLoading: false,
      isLoaded: false,
      isBusy: false,
      includeDisabled: false,
      typeFilter: null,
      expandedTracks: {},
      expandedNested: {},
      tracksByAlbum: {},
      tracksLoading: {},
      editingYearId: null,
      yearDraft: '',
      coverModal: { show: false, loading: false, album: null, covers: [] },
      relationsModal: { show: false, album: null, type: 'remaster_of', relatedAlbumId: null },
      mergeModal: { show: false, album: null, sourceAlbumId: null },
      suggestionsModal: { show: false, loading: false, items: [] },
      coverPreview: { show: false, album: null },
    }
  },
  computed: {
    artistId() {
      return this.artist ? this.artist.id : null
    },
    canManage() {
      return this.$can('manage', 'artist-tracks')
    },
    canViewCovers() {
      return this.$can('view', 'artist-covers')
    },
    hasMusicBrainz() {
      return Boolean(this.artist && this.artist.external_ids && this.artist.external_ids.musicbrainz)
    },
    typeOptions() {
      return ALBUM_TYPES
    },
    relationTypeOptions() {
      return RELATION_TYPES
    },
    typeFilterOptions() {
      return [{ value: null, text: 'Все типы' }, ...ALBUM_TYPES]
    },
    // Все альбомы артиста, включая вложенные издания и синглы (плоский список)
    flatAlbums() {
      const result = []
      this.albums.forEach(album => {
        result.push(album)
        album.editions.forEach(edition => result.push(edition))
        album.singles.forEach(single => result.push(single))
      })
      return result
    },
    // Строки таблицы: оригиналы (с фильтром по типу), под раскрытым — вложенные издания/синглы
    rows() {
      const result = []
      this.albums
        .filter(album => !this.typeFilter || album.type === this.typeFilter)
        .forEach(album => {
          result.push({ key: `a${album.id}`, album, depth: 0, kindLabel: null })
          if (this.isNestedExpanded(album.id, 'editions')) {
            album.editions.forEach(edition => result.push({
              key: `a${album.id}-e${edition.id}`,
              album: edition,
              depth: 1,
              kindLabel: this.relationLabelTo(edition, album.id) || 'Издание',
            }))
          }
          if (this.isNestedExpanded(album.id, 'singles')) {
            album.singles.forEach(single => result.push({
              key: `a${album.id}-s${single.id}`,
              album: single,
              depth: 1,
              kindLabel: 'Сингл с альбома',
            }))
          }
        })
      return result
    },
    currentRelations() {
      if (!this.relationsModal.album) return []
      const fresh = this.flatAlbums.find(album => album.id === this.relationsModal.album.id)
      return fresh ? fresh.relations : []
    },
  },
  methods: {
    // Публичный: Task 13 зовёт после импорта
    async reload() {
      if (!this.artistId) return
      this.isLoading = true
      try {
        const response = await this.$store.dispatch('artists/fetchArtistAlbums', {
          id: this.artistId,
          includeDisabled: this.includeDisabled,
        })
        this.albums = response.data
        this.isLoaded = true
        // Состав треков мог поменяться (пересопоставление, объединение) — кеш трек-листов сбрасываем
        this.tracksByAlbum = {}
        Object.keys(this.expandedTracks).forEach(albumId => {
          if (this.expandedTracks[albumId]) this.loadTracks(Number(albumId))
        })
      } catch (error) {
        this.notifyError(error, 'Не удалось загрузить альбомы')
      } finally {
        this.isLoading = false
      }
    },
    // Публичный: первая загрузка при открытии вкладки
    loadOnce() {
      if (!this.isLoaded && !this.isLoading) this.reload()
    },
    notify(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    },
    notifyError(error, fallback) {
      const message = error && error.response && error.response.data && error.response.data.error
      this.notify(message || fallback, 'danger')
    },
    // Выполняет запрос и перезагружает список; false — запрос упал (тост уже показан)
    async perform(actionName, payload, errorTitle) {
      this.isBusy = true
      try {
        const response = await this.$store.dispatch(`artists/${actionName}`, { id: this.artistId, ...payload })
        return response
      } catch (error) {
        this.notifyError(error, errorTitle)
        return null
      } finally {
        this.isBusy = false
      }
    },
    trackTotal(album) {
      return album.tracks_total || album.total_tracks || 0
    },
    pluralEditions(count) {
      return count === 1 ? 'издание' : (count >= 2 && count <= 4 ? 'издания' : 'изданий')
    },
    pluralSingles(count) {
      return count === 1 ? 'сингл' : (count >= 2 && count <= 4 ? 'сингла' : 'синглов')
    },
    relationLabelTo(album, originalId) {
      const relation = album.relations.find(item => item.related_album_id === originalId)
      return relation ? relation.type_label : null
    },
    relationTypeLabel(type) {
      const found = RELATION_TYPES.find(item => item.value === type)
      return found ? found.text.toLowerCase() : type
    },
    albumTitle(albumId) {
      const album = this.flatAlbums.find(item => item.id === albumId)
      return album ? album.title : `#${albumId}`
    },
    otherAlbumOptions(excludeId) {
      const options = this.flatAlbums
        .filter(album => album.id !== excludeId)
        .map(album => ({
          value: album.id,
          text: `${album.title}${album.effective_year ? ` (${album.effective_year})` : ''}`,
        }))
      return [{ value: null, text: 'Выберите альбом…' }, ...options]
    },

    // --- правка полей ---
    async updateAlbumField(album, data) {
      const response = await this.perform('updateAlbum', { albumId: album.id, data }, 'Не удалось сохранить альбом')
      // При ошибке тоже перечитываем: контролы (select/switch) уже показывают новое значение
      await this.reload()
      return response
    },
    startEditYear(album) {
      if (!this.canManage) return
      this.yearDraft = album.original_year ? String(album.original_year) : ''
      this.editingYearId = album.id
    },
    async saveYear(album) {
      // blur после Enter/Esc не должен сохранять повторно
      if (this.editingYearId !== album.id) return
      this.editingYearId = null
      const draft = String(this.yearDraft).trim()
      const year = draft === '' ? null : Number(draft)
      if (year === album.original_year) return
      if (year !== null && (!Number.isInteger(year) || year < 1900 || year > 2100)) {
        this.notify('Год должен быть от 1900 до 2100', 'danger')
        return
      }
      await this.updateAlbumField(album, { original_year: year })
    },

    // --- трек-лист ---
    isTracksExpanded(albumId) {
      return Boolean(this.expandedTracks[albumId])
    },
    toggleTracks(album) {
      const expanded = !this.expandedTracks[album.id]
      this.$set(this.expandedTracks, album.id, expanded)
      if (expanded && !this.tracksByAlbum[album.id]) this.loadTracks(album.id)
    },
    async loadTracks(albumId) {
      this.$set(this.tracksLoading, albumId, true)
      try {
        const response = await this.$store.dispatch('artists/fetchDbAlbumTracks', { id: this.artistId, albumId })
        this.$set(this.tracksByAlbum, albumId, response.data)
      } catch (error) {
        this.notifyError(error, 'Не удалось загрузить трек-лист')
      } finally {
        this.$set(this.tracksLoading, albumId, false)
      }
    },
    isNestedExpanded(albumId, kind) {
      return Boolean(this.expandedNested[`${albumId}-${kind}`])
    },
    toggleNested(albumId, kind) {
      const key = `${albumId}-${kind}`
      this.$set(this.expandedNested, key, !this.expandedNested[key])
    },
    // «диск.позиция» только если дисков несколько
    trackNumber(albumId, albumTrack) {
      const tracks = this.tracksByAlbum[albumId] || []
      const hasSeveralDiscs = tracks.some(item => item.disc_number !== tracks[0].disc_number)
      return hasSeveralDiscs ? `${albumTrack.disc_number}.${albumTrack.position}` : String(albumTrack.position)
    },
    playerKey(albumTrack) {
      return `track-${albumTrack.track_id}`
    },
    isPlaying(albumTrack) {
      return isTrackPlaying(this.playerKey(albumTrack))
    },
    playAlbumTrack(album, albumTrack) {
      toggleTrack({
        key: this.playerKey(albumTrack),
        url: albumTrack.track_download_url,
        title: albumTrack.track_title || albumTrack.title,
        artist: albumTrack.artists_label || album.artists_label || (this.artist ? this.artist.title : ''),
        sourceLabel: 'Файл трека',
        link: this.artist ? { name: 'artist-view', params: { id: this.artist.id } } : null,
        details: [album.title, album.effective_year ? String(album.effective_year) : null],
        hint: null,
      })
    },

    // --- обложки ---
    async openCoverModal(album) {
      this.coverModal = { show: true, loading: true, album, covers: [] }
      try {
        const response = await this.$store.dispatch('artists/fetchArtistCovers', { id: this.artistId })
        this.coverModal.covers = response.data
      } catch (error) {
        this.notifyError(error, 'Не удалось загрузить обложки артиста')
      } finally {
        this.coverModal.loading = false
      }
    },
    openCoverPreview(album) {
      if (!album.processed_cover && !album.cover_url) return
      this.coverPreview = { show: true, album }
    },
    async bindCover(cover) {
      const album = this.coverModal.album
      this.coverModal.show = false
      await this.updateAlbumField(album, { artist_cover_id: cover.id })
    },
    async unbindCover(album) {
      await this.updateAlbumField(album, { artist_cover_id: null })
    },

    // --- связи ---
    openRelationsModal(album) {
      this.relationsModal = { show: true, album, type: 'remaster_of', relatedAlbumId: null }
    },
    async addRelation() {
      const response = await this.perform('createAlbumRelation', {
        albumId: this.relationsModal.album.id,
        relatedAlbumId: this.relationsModal.relatedAlbumId,
        type: this.relationsModal.type,
      }, 'Не удалось добавить связь')
      if (response) {
        this.relationsModal.relatedAlbumId = null
        await this.reload()
      }
    },
    async removeRelation(relation) {
      const response = await this.perform('deleteAlbumRelation', { relationId: relation.id }, 'Не удалось удалить связь')
      if (response) await this.reload()
    },

    // --- объединение / удаление ---
    openMergeModal(album) {
      this.mergeModal = { show: true, album, sourceAlbumId: null }
    },
    async confirmMerge() {
      const target = this.mergeModal.album
      const source = this.flatAlbums.find(album => album.id === this.mergeModal.sourceAlbumId)
      if (!target || !source) return

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Влить «${source.title}» (треков: ${this.trackTotal(source)}) в «${target.title}» (треков: ${this.trackTotal(target)})? `
        + `Альбом «${source.title}» будет удалён.`,
        { title: 'Объединить альбомы', okTitle: 'Объединить', cancelTitle: 'Отмена', okVariant: 'danger' },
      )
      if (!confirmed) return

      const response = await this.perform('mergeAlbum', {
        albumId: target.id,
        sourceAlbumId: source.id,
      }, 'Не удалось объединить альбомы')
      if (response) {
        this.mergeModal.show = false
        this.notify('Альбомы объединены')
        await this.reload()
      }
    },
    async removeAlbum(album) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Удалить альбом «${album.title}»? Релиз запомнится и не будет импортирован снова.`,
        { title: 'Удалить альбом', okTitle: 'Удалить', cancelTitle: 'Отмена', okVariant: 'danger' },
      )
      if (!confirmed) return

      const response = await this.perform('deleteAlbum', { albumId: album.id }, 'Не удалось удалить альбом')
      if (response) {
        this.notify('Альбом удалён')
        await this.reload()
      }
    },

    // --- массовые действия ---
    bindCoversMessage(data) {
      return `Привязано обложек: ${data.bound}`
    },
    rematchMessage(data) {
      return `Сопоставлено треков: ${data.linked}`
    },
    musicBrainzMessage() {
      return 'Альбомы уточнены по MusicBrainz'
    },
    async runBulkAction(actionName, title, buildMessage) {
      const response = await this.perform(actionName, {}, `${title}: не удалось выполнить`)
      if (response) {
        this.notify(buildMessage(response.data || {}))
        await this.reload()
      }
    },

    // --- подсказки связей ---
    async openSuggestions() {
      this.suggestionsModal = { show: true, loading: true, items: [] }
      try {
        const response = await this.$store.dispatch('artists/fetchAlbumRelationSuggestions', { id: this.artistId })
        this.suggestionsModal.items = response.data
      } catch (error) {
        this.notifyError(error, 'Не удалось загрузить подсказки')
      } finally {
        this.suggestionsModal.loading = false
      }
    },
    removeSuggestion(suggestion) {
      this.suggestionsModal.items = this.suggestionsModal.items.filter(item => item !== suggestion)
    },
    async acceptSuggestion(suggestion) {
      const response = await this.perform('createAlbumRelation', {
        albumId: suggestion.album_id,
        relatedAlbumId: suggestion.related_album_id,
        type: suggestion.type,
      }, 'Не удалось принять подсказку')
      if (response) {
        this.removeSuggestion(suggestion)
        await this.reload()
      }
    },
    async dismissSuggestion(suggestion) {
      const response = await this.perform('dismissAlbumRelation', {
        albumId: suggestion.album_id,
        relatedAlbumId: suggestion.related_album_id,
        type: suggestion.type,
      }, 'Не удалось отклонить подсказку')
      if (response) this.removeSuggestion(suggestion)
    },
  },
}
</script>

<style scoped>
.albums-type-filter {
  width: 200px;
}

.albums-table td {
  vertical-align: middle;
}

.album-disabled {
  opacity: 0.5;
}

.album-nested {
  background: rgba(128, 128, 128, 0.06);
}

.album-title {
  color: inherit;
}

.album-thumb {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 40px;
}

.album-thumb img,
.album-thumb-empty {
  display: block;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
}

.album-thumb-clickable {
  cursor: zoom-in;
}

.album-cover-preview {
  margin: 0;
  max-width: 100%;
  text-align: center;
}

.album-cover-preview img {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  border-radius: 6px;
}

.album-thumb-empty {
  background: rgba(128, 128, 128, 0.3);
}

.album-thumb-ok {
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 50%;
  background: #28c76f;
  color: #fff;
}

.album-tracklist {
  margin: 4px 0 8px 56px;
  font-size: 13px;
}

.album-track-row {
  display: flex;
  align-items: center;
  padding: 2px 0;
}

.album-track-number {
  width: 40px;
  opacity: 0.6;
}

.album-cover-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 12px;
}

.album-cover-choice img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 4px;
}
</style>
